import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '@/context/CartContext';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navbar() {
  const { t } = useTranslation();
  const { count, openCart } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const solid = scrolled || open;

  const navLinks = [
    { id: 'home', label: t('nav.home') },
    { id: 'about', label: t('nav.about') },
    { id: 'menu', label: t('nav.menu') },
    { id: 'gallery', label: t('nav.gallery') },
    { id: 'hours', label: t('nav.hours') },
    { id: 'contact', label: t('nav.contact') },
  ];

  const logoClasses = solid
    ? 'text-foreground-950'
    : 'text-background-50';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? 'bg-background-50/95 backdrop-blur shadow-sm' : 'bg-transparent'
      }`}
    >
      <nav className="w-full">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6 py-3 md:py-4">
          <a
            href="#home"
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
            aria-label="Mehfil 24/7 home"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500">
              <i className="ri-restaurant-2-line text-xl text-background-50"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className={`font-heading text-xl font-semibold ${logoClasses}`}>Mehfil</span>
              <span className={`text-[11px] tracking-[0.3em] uppercase ${solid ? 'text-primary-600' : 'text-accent-300'}`}>
                24 / 7
              </span>
            </span>
          </a>

          <div className="hidden items-center gap-7 md:flex">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-primary-500 whitespace-nowrap ${
                  solid ? 'text-foreground-800' : 'text-background-50'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="hidden items-center gap-3 md:flex">
            <LanguageSwitcher light={!solid} />
            <button
              type="button"
              onClick={openCart}
              className={`relative inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                solid
                  ? 'border-background-300/70 text-foreground-800 hover:bg-background-100'
                  : 'border-background-50/50 text-background-50 hover:bg-background-50/10'
              }`}
              aria-label={t('nav.cart')}
            >
              <i className="ri-shopping-bag-3-line text-lg"></i>
              {t('nav.cart')}
              {count > 0 && (
                <span className="ml-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1 text-xs font-semibold text-background-50">
                  {count}
                </span>
              )}
            </button>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-bold uppercase tracking-wide text-background-50 whitespace-nowrap ring-2 ring-primary-300/60 transition-all duration-300 hover:bg-primary-600 hover:ring-primary-200 hover:scale-105"
            >
              <i className="ri-smartphone-line"></i>
              {t('nav.order')}
            </a>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitcher light={!solid} />
            <button
              type="button"
              onClick={openCart}
              className={`relative flex h-10 w-10 items-center justify-center rounded-full ${
                solid ? 'text-foreground-950 bg-background-200' : 'text-background-50 bg-background-950/20'
              }`}
              aria-label={t('nav.cart')}
            >
              <i className="ri-shopping-bag-3-line text-lg"></i>
              {count > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1 text-xs font-semibold text-background-50">
                  {count}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className={`flex h-10 w-10 items-center justify-center rounded-full md:hidden ${
                solid ? 'text-foreground-950 bg-background-200' : 'text-background-50 bg-background-950/20'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`ri-${open ? 'close-line' : 'menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>

        {open && (
          <div className="border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-foreground-800"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50"
              >
                <i className="ri-smartphone-line"></i>
                {t('nav.order')}
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}